import * as React from 'react';
import './settings-privacy.css';

// The Privacy section of Settings, from props only: the switch for the
// anonymous usage data, what it means, why it may be unavailable, and where
// a save stands. The container in the entry point decides the state from the
// consent record and the load's decision.

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/** Whether analytics can run at all here: a stream for the hostname, and no privacy signal from the browser. */
export type PrivacyAvailability = 'available' | 'no-stream' | 'declined';

/**
 * Where the switch stands: `on` by a recorded grant, `implied` on without a
 * record (outside the regions that require consent first), `off` by a
 * recorded denial or while the sheet waits for an answer; undefined while
 * the record and the region are read.
 */
export type PrivacyState = 'on' | 'implied' | 'off';

export interface PrivacyFormProps {
  availability: PrivacyAvailability;
  state?: PrivacyState;
  status: SaveStatus;
  error?: string;
  onChange(enabled: boolean): void;
}

export const PrivacyForm: React.FC<PrivacyFormProps> = ({ availability, state, status, error, onChange }) => {
  const available = availability === 'available';
  const on = available && (state === 'on' || state === 'implied');
  const busy = status === 'saving' || (available && state === undefined);
  return (
    <section className="panel settings-privacy">
      <h2>Privacy</h2>
      <div className="settings-privacy-form">
        <label className="settings-privacy-switch">
          <input type="checkbox" data-track="settings.privacy.toggle" data-track-value={on ? 'off' : 'on'} checked={on} disabled={busy || !available} onChange={(e) => onChange(e.target.checked)} />
          <span>Share anonymous usage data</span>
        </label>
        <p className="settings-privacy-help">
          What is sent is which screens and buttons are used, never your games, your username or your moves. Turning it off stops it at once.
        </p>
        <p className="settings-privacy-note" data-kind={availability === 'available' ? (state === 'implied' ? 'implied' : 'none') : availability}>
          {availability === 'no-stream' && 'Usage data is not collected on this address.'}
          {availability === 'declined' && 'Your browser asks not to be tracked, so nothing is collected.'}
          {available && state === 'implied' && 'On by default outside Europe; turn it off here at any time.'}
        </p>
        <span className="settings-privacy-status" data-status={status}>
          {status === 'saving' && 'Saving…'}
          {status === 'saved' && 'Saved'}
          {status === 'error' && (error ?? 'Could not save')}
        </span>
      </div>
    </section>
  );
};
